import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface EclipseAlignerProps {
  width?: number;
  height?: number;
}

export const EclipseAligner: React.FC<EclipseAlignerProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);
  const [eclipseType, setEclipseType] = useState<'Solar Eclipse' | 'Lunar Eclipse' | 'No Eclipse'>('No Eclipse');

  const sunPos = { x: 40, y: size / 2 };
  const earthPos = { x: size / 2 + 30, y: size / 2 };
  
  const moonRef = useRef({ x: size / 2 - 30, y: size / 2 - 80 });
  const isDragging = useRef(false);
  const canvasElementRef = useRef<HTMLCanvasElement | null>(null);

  // Set the canvas ref to bind events
  const setCanvasRef = (el: HTMLCanvasElement | null) => {
    canvasElementRef.current = el;
  };

  useEffect(() => {
    const handleDown = (e: MouseEvent | TouchEvent) => {
      if (!canvasElementRef.current) return;
      const rect = canvasElementRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      
      const dx = x - moonRef.current.x;
      const dy = y - moonRef.current.y;
      if (Math.sqrt(dx * dx + dy * dy) < 30) {
        isDragging.current = true;
      }
    };

    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!isDragging.current || !canvasElementRef.current) return;
      e.preventDefault();
      const rect = canvasElementRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      moonRef.current.x = clientX - rect.left;
      moonRef.current.y = clientY - rect.top;

      // Snap logic
      if (Math.abs(moonRef.current.y - sunPos.y) < 20) {
        moonRef.current.y += (sunPos.y - moonRef.current.y) * 0.2;
      }
    };

    const handleUp = () => {
      isDragging.current = false;
    };

    const canvas = canvasElementRef.current;
    if (canvas) {
      canvas.addEventListener('mousedown', handleDown);
      window.addEventListener('mousemove', handleMove, { passive: false });
      window.addEventListener('mouseup', handleUp);
      
      canvas.addEventListener('touchstart', handleDown);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleUp);
    }

    return () => {
      if (canvas) {
        canvas.removeEventListener('mousedown', handleDown);
        canvas.removeEventListener('touchstart', handleDown);
      }
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, [sunPos.y]);

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number, canvas: HTMLCanvasElement) => {
    // Bind ref so events can attach
    if (canvasElementRef.current !== canvas) {
      setCanvasRef(canvas);
    }

    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const mX = moonRef.current.x;
    const mY = moonRef.current.y;

    // Alignment Line
    ctx.beginPath();
    ctx.setLineDash([5, 5]);
    ctx.moveTo(sunPos.x, sunPos.y);
    ctx.lineTo(size - 20, sunPos.y);
    ctx.strokeStyle = 'rgba(255,255,255,0.2)';
    ctx.stroke();
    ctx.setLineDash([]);

    let currentEclipse: 'Solar Eclipse' | 'Lunar Eclipse' | 'No Eclipse' = 'No Eclipse';

    // Solar Eclipse Check
    const isAlignedY = Math.abs(mY - sunPos.y) < 15;
    if (isAlignedY) {
      if (mX > sunPos.x + 30 && mX < earthPos.x - 10) {
        currentEclipse = 'Solar Eclipse';
        
        // Draw shadow cone from Moon to Earth
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.beginPath();
        ctx.moveTo(mX, mY - 10);
        ctx.lineTo(earthPos.x, earthPos.y - 12);
        ctx.lineTo(earthPos.x, earthPos.y + 12);
        ctx.lineTo(mX, mY + 10);
        ctx.fill();
      } else if (mX > earthPos.x + 15) {
        currentEclipse = 'Lunar Eclipse';
        
        // Draw shadow cone from Earth over Moon
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.beginPath();
        ctx.moveTo(earthPos.x, earthPos.y - 15);
        ctx.lineTo(size, earthPos.y - 25);
        ctx.lineTo(size, earthPos.y + 25);
        ctx.lineTo(earthPos.x, earthPos.y + 15);
        ctx.fill();
      }
    }

    // Update state without thrashing
    setEclipseType((prev) => (prev !== currentEclipse ? currentEclipse : prev));

    // Draw Sun
    const sunGrad = ctx.createRadialGradient(sunPos.x, sunPos.y, 10, sunPos.x, sunPos.y, 40);
    sunGrad.addColorStop(0, '#FFFFFF');
    sunGrad.addColorStop(0.3, '#FDE047');
    sunGrad.addColorStop(1, 'rgba(253, 224, 71, 0)');
    ctx.fillStyle = sunGrad;
    ctx.beginPath();
    ctx.arc(sunPos.x, sunPos.y, 40, 0, Math.PI * 2);
    ctx.fill();

    // Draw Earth
    ctx.fillStyle = '#4A90D9';
    ctx.beginPath();
    ctx.arc(earthPos.x, earthPos.y, 15, 0, Math.PI * 2);
    ctx.fill();

    // Draw Moon
    ctx.fillStyle = '#A0A0A0';
    ctx.beginPath();
    ctx.arc(mX, mY, 8, 0, Math.PI * 2);
    ctx.fill();

    // Interaction Hint
    if (!isDragging.current && currentEclipse === 'No Eclipse') {
      ctx.fillStyle = `rgba(255, 255, 255, ${Math.abs(Math.sin(frameCount * 0.05)) * 0.5 + 0.2})`;
      ctx.textAlign = 'center';
      ctx.font = '12px sans-serif';
      ctx.fillText('Drag the Moon', mX, mY - 20);
    }

  }, [size, sunPos.x, sunPos.y, earthPos.x, earthPos.y]);

  const canvasHookRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size - 60, position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #1E293B', cursor: isDragging.current ? 'grabbing' : 'grab' }}>
        <canvas ref={canvasHookRef} />
      </div>
      
      <div style={{ 
        marginTop: '16px', 
        width: '100%', 
        textAlign: 'center',
        height: '30px',
        fontSize: '18px',
        fontWeight: 'bold',
        color: eclipseType === 'No Eclipse' ? '#94A3B8' : '#FDE047',
        transition: 'color 0.2s'
      }}>
        {eclipseType}
      </div>
    </div>
  );
};
